import React, { useState } from 'react'
import ReportLayout from '../../layouts/Report'

import useGetSchools from '../../../hooks/schools/useGetSchools'
import useGetCampuses from '../../../hooks/campuses/useGetCampuses'
import useGetUserVisitsReport from '../../../hooks/analytics/useGetUserVisitsReport'
import useExportUserVisitsReport from '../../../hooks/analytics/useExportUserVisitsReport'

import BarChart from '../../sub_components/charts/BarChart'
import ReportTablet from '../../sub_components/ReportTablet'
import ReportDatePicker from '../../sub_components/ReportDatePicker'
import FilterTable from '../../sub_components/FilterTable'

import { getDateString } from '../../../helpers/utility'

import { IColumnProps, IFilterableDataProps } from '../../../interfaces/table'


const UserVisitsReport = () => {
    const [schoolFilters, setSchoolFilters] = useState<number[]>([]);
    const [campusFilters, setCampusFilters] = useState<number[]>([]);
    const [startDate, setStartDate] = useState<string>("");
    const [endDate, setEndDate] = useState<string>("");

    const filterQuery = new URLSearchParams();
    filterQuery.set("start_date", startDate);
    filterQuery.set("end_date", endDate);
    schoolFilters.forEach((id) => filterQuery.append("school_ids", id.toString()));
    campusFilters.forEach((id) => filterQuery.append("campus_ids", id.toString()));

    const { data: schools } = useGetSchools();
    const { data: campuses } = useGetCampuses();
    const { data: userVisitsReport } = useGetUserVisitsReport(filterQuery.toString());

    const { mutate: exportCsv } = useExportUserVisitsReport();

    let filterTableDataColumns__Schools: IColumnProps[] = [
        { name: "ID", identifier: "id" },
        { name: "School Name", identifier: "name" },
    ];
    let filterTableDataColumns__Campuses: IColumnProps[] = [
        { name: "ID", identifier: "id" },
        { name: "Campus Name", identifier: "name" },
    ];
    let filterTableDataColumns__UserVisitsReport: IColumnProps[] = [
        { name: "ID", identifier: "id" },
        { name: "Full Name", identifier: "full_name" },
        { name: "Email", identifier: "email" },
        { name: "Visits", identifier: "visits" },
        { name: "Last Visit", identifier: "last_visit" },
    ];

    let filterTableData__Schools: IFilterableDataProps[] = [];
    let filterTableData__Campuses: IFilterableDataProps[] = [];
    let filterTableData__UserVisitsReport: IFilterableDataProps[] = [];

    if (schools) {
        filterTableData__Schools = schools.map((school) => ({
            id: school.id,
            name: school.name,
        }));
    }

    if (campuses) {
        filterTableData__Campuses = campuses.map((campus) => ({
            id: campus.id,
            name: campus.name,
        }));
    }

    if (userVisitsReport) {
        filterTableData__UserVisitsReport = userVisitsReport.table_data.map((user) => ({
            id: user.id,
            full_name: user.full_name,
            email: user.email,
            visits: user.visits,
            last_visit: getDateString(user.last_visit),
        }));
    }

    const onExportCsvReport = () => {
        exportCsv({
            payload: {
                filterQuery: filterQuery.toString(),
            }
        });
    }

    return (
        <ReportLayout>
            <div className='immersion_admin__careers_report'>
                <div className='immersion_admin__careers_report__header'>
                    <div className='immersion_admin__careers_report__header__content'>
                        <h2 className='immersion_admin__careers_report__header__content__title'>User Visits Report</h2>
                        <p className='immersion_admin__careers_report__header__content__description'>
                            This slide gives an overview of users who have visited the platform since the start of data collection (20/04/24).
                            The chart to the bottom left shows a cumulative timeline of visits to the platform.
                            The table to the bottom right shows the latest date a user has had activity on the platform.
                        </p>
                    </div>
                    <div className='immersion_admin__careers_report__header__actions'>
                        <button className='immersion_admin__btn__primary immersion_admin__careers_report__header__actions__button' onClick={onExportCsvReport}>Export CSV</button>
                    </div>
                </div>
                <div className='immersion_admin__careers_report__chart_body'>
                    <div className='immersion_admin__careers_report__chart__row'>
                        <FilterTable
                            columns={filterTableDataColumns__Schools}
                            data={filterTableData__Schools}
                            enableFilters={true}
                            filters={schoolFilters}
                            setFilters={setSchoolFilters}
                        />
                        <FilterTable
                            columns={filterTableDataColumns__Campuses}
                            data={filterTableData__Campuses}
                            enableFilters={true}
                            filters={campusFilters}
                            setFilters={setCampusFilters}
                        />
                        <div className='immersion_admin__careers_report__chart__row immersion_admin__careers_report__chart__row__vertical'>
                            <ReportDatePicker
                                required={true}
                                startDate={startDate}
                                setStartDate={setStartDate}
                                endDate={endDate}
                                setEndDate={setEndDate}
                            />
                            <div className='immersion_admin__careers_report__chart__row'>
                                <ReportTablet
                                    title={userVisitsReport?.total_unique_visits?.toString() ?? "0"}
                                    description='Total Unique Visits'
                                    color={'green'}
                                />
                            </div>
                        </div>
                    </div>
                    <div className='immersion_admin__careers_report__chart__row'>
                        <FilterTable
                            columns={filterTableDataColumns__UserVisitsReport}
                            data={filterTableData__UserVisitsReport}
                            enableFilters={false}
                            filters={[]}
                            setFilters={() => { }}
                        />
                        {userVisitsReport?.page_view_chart_data.length ? (
                            <BarChart
                                width={600}
                                height={318}
                                data={userVisitsReport?.page_view_chart_data ?? []}
                                xLabel="Date"
                                yLabel="Number of Visits"
                                xTickAngle={90}
                                xInterval={0}
                            />
                        ) : (
                            <div className='immersion_admin__careers_report__chart__no_data'>
                                No analytics data available
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </ReportLayout>
    )
}

export default UserVisitsReport;